/**
 * Drop Can — descent capsule for deploy phase.
 *
 * Two visual states:
 * - Descent: tapered cylinder with heat shield, retro nozzles, team stripe
 * - Crashed: tilted body, blown hatch, scorch marks, debris
 *
 * Animation state machine: approach → descend → landed → done
 * Reuses orbital approach path computation from droneDelivery.
 */

import * as THREE from 'three';
import type { Vec3, PlanetState, TeamPalette } from '../types';
import { buildDropCanDescentBody, buildDropCanCrashedBody } from './transitModels';
import { DROP_CAN_APPROACH_DURATION, DROP_CAN_DESCEND_DURATION } from '../constants';
import { getSurfaceRadius } from '../core/physics';

// ---------------------------------------------------------------------------
// Tuning Constants
// ---------------------------------------------------------------------------

const MIN_ENTRY_RADIUS = 60;
const ARRIVAL_ALT = 4.0;
const PATH_SAMPLES = 100;
const ARC_LIFT = 0.35;        // control point lift as fraction of chord length
const PLANET_SAFE_MULT = 1.8; // safety margin for planet avoidance

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DropCanEffect {
  rootGroup: THREE.Group;
  descentModel: THREE.Group;
  crashedModel: THREE.Group | null;
  flame: THREE.Group;
  flameLight: THREE.PointLight | null;

  state: 'approach' | 'descend' | 'landed' | 'done';
  stateTime: number;
  startDelay: number;  // stagger delay before animation begins
  approachSoundPlayed: boolean;
  landedSoundPlayed: boolean;

  orbitalPath: THREE.Vector3[];
  approachDuration: number;

  planetCenter: THREE.Vector3;
  surfaceNormal: THREE.Vector3;
  surfaceRadius: number;
  surfaceQuat: THREE.Quaternion;

  teamColor: TeamPalette;

  // Deferred path computation — computed lazily when stagger delay expires
  _deferredArrival: Vec3 | null;
  _deferredTargetIdx: number;
  _deferredPlanets: PlanetState[] | null;

  // Reusable temp objects to avoid per-frame allocations
  _tmpPos: THREE.Vector3;
  _tmpVec: THREE.Vector3;
  _tmpVec2: THREE.Vector3;
  _tmpQuat: THREE.Quaternion;
  _tmpEuler: THREE.Euler;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// Orbital Approach Path (simplified from droneDelivery)
// ---------------------------------------------------------------------------

function computeDropApproach(
  arrivalPoint: Vec3,
  targetPlanetIndex: number,
  planets: PlanetState[],
): Vec3[] {
  // Mass-weighted centroid
  let totalMass = 0;
  let cx = 0, cy = 0, cz = 0;
  for (const p of planets) {
    if (!p.alive) continue;
    totalMass += p.mass;
    cx += p.position.x * p.mass;
    cy += p.position.y * p.mass;
    cz += p.position.z * p.mass;
  }
  if (totalMass > 0) { cx /= totalMass; cy /= totalMass; cz /= totalMass; }

  // Entry radius scales with system extent so drop cans start beyond all planets
  let maxExtent = 0;
  for (const p of planets) {
    if (!p.alive) continue;
    const ex = p.position.x - cx, ey = p.position.y - cy, ez = p.position.z - cz;
    const d = Math.sqrt(ex * ex + ey * ey + ez * ez) + p.radius;
    if (d > maxExtent) maxExtent = d;
  }
  const ENTRY_RADIUS = Math.max(MIN_ENTRY_RADIUS, maxExtent * 1.8);

  // Direction from centroid toward arrival
  const dx = arrivalPoint.x - cx;
  const dy = arrivalPoint.y - cy;
  const dz = arrivalPoint.z - cz;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const dirX = dist > 0.001 ? dx / dist : 1;
  const dirY = dist > 0.001 ? dy / dist : 0;
  const dirZ = dist > 0.001 ? dz / dist : 0;

  // Build orthonormal basis perpendicular to arrival direction
  const arb = Math.abs(dirY) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  let ux = dirY * arb.z - dirZ * arb.y;
  let uy = dirZ * arb.x - dirX * arb.z;
  let uz = dirX * arb.y - dirY * arb.x;
  const uLen = Math.sqrt(ux * ux + uy * uy + uz * uz);
  if (uLen > 0.001) { ux /= uLen; uy /= uLen; uz /= uLen; }
  const vx = dirY * uz - dirZ * uy;
  const vy = dirZ * ux - dirX * uz;
  const vz = dirX * uy - dirY * ux;

  // Entry direction: vary per planet index for visual variety
  const sweepAngle = (targetPlanetIndex / Math.max(planets.length, 1)) * Math.PI * 2 + Math.PI * 0.3;
  const cosA = Math.cos(sweepAngle);
  const sinA = Math.sin(sweepAngle);
  const entryOffX = cosA * ux + sinA * vx;
  const entryOffY = cosA * uy + sinA * vy;
  const entryOffZ = cosA * uz + sinA * vz;

  // P0: entry point far from centroid, offset perpendicular for a sweeping arc
  const P0: Vec3 = {
    x: cx + entryOffX * ENTRY_RADIUS,
    y: cy + entryOffY * ENTRY_RADIUS,
    z: cz + entryOffZ * ENTRY_RADIUS,
  };
  const P2 = arrivalPoint;

  // Chord direction P0→P2
  const chordX = P2.x - P0.x;
  const chordY = P2.y - P0.y;
  const chordZ = P2.z - P0.z;
  const chordLen = Math.sqrt(chordX * chordX + chordY * chordY + chordZ * chordZ);

  // Lift direction: perpendicular to chord, cross with basis vector
  let lx = chordY * uz - chordZ * uy;
  let ly = chordZ * ux - chordX * uz;
  let lz = chordX * uy - chordY * ux;
  const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);
  if (lLen > 0.001) { lx /= lLen; ly /= lLen; lz /= lLen; }
  else { lx = ux; ly = uy; lz = uz; }

  // P1: control point at chord midpoint, lifted perpendicular for arc curvature
  const liftAmount = chordLen * ARC_LIFT;
  let p1x = (P0.x + P2.x) / 2 + lx * liftAmount;
  let p1y = (P0.y + P2.y) / 2 + ly * liftAmount;
  let p1z = (P0.z + P2.z) / 2 + lz * liftAmount;

  // Planet avoidance: deflect control point away from any planet the arc clips
  // Uses heightmap-aware radius for accurate terrain clearance
  for (let attempt = 0; attempt < 6; attempt++) {
    let adjusted = false;
    for (let pi = 0; pi < planets.length; pi++) {
      if (pi === targetPlanetIndex || !planets[pi].alive) continue;
      const pp = planets[pi].position;

      for (let s = 1; s < 40; s++) {
        const t = s / 40;
        const t1 = 1 - t;
        const sx = t1 * t1 * P0.x + 2 * t1 * t * p1x + t * t * P2.x;
        const sy = t1 * t1 * P0.y + 2 * t1 * t * p1y + t * t * P2.y;
        const sz = t1 * t1 * P0.z + 2 * t1 * t * p1z + t * t * P2.z;
        const sdx = sx - pp.x;
        const sdy = sy - pp.y;
        const sdz = sz - pp.z;
        const sDist = Math.sqrt(sdx * sdx + sdy * sdy + sdz * sdz);

        // Use heightmap-aware surface radius for the direction from planet center
        const surfR = getSurfaceRadius(planets[pi], { x: sdx, y: sdy, z: sdz });
        const safeR = surfR * PLANET_SAFE_MULT;

        if (sDist < safeR) {
          // Push control point away from this planet
          const pushX = p1x - pp.x;
          const pushY = p1y - pp.y;
          const pushZ = p1z - pp.z;
          const pushLen = Math.sqrt(pushX * pushX + pushY * pushY + pushZ * pushZ);
          if (pushLen > 0.001) {
            const pushAmount = (safeR - sDist) * 2;
            p1x += (pushX / pushLen) * pushAmount;
            p1y += (pushY / pushLen) * pushAmount;
            p1z += (pushZ / pushLen) * pushAmount;
          }
          adjusted = true;
          break;
        }
      }
    }
    if (!adjusted) break;
  }

  // Generate PATH_SAMPLES points along the quadratic Bezier
  const result: Vec3[] = [];
  for (let i = 0; i < PATH_SAMPLES; i++) {
    const t = i / (PATH_SAMPLES - 1);
    const t1 = 1 - t;
    result.push({
      x: t1 * t1 * P0.x + 2 * t1 * t * p1x + t * t * P2.x,
      y: t1 * t1 * P0.y + 2 * t1 * t * p1y + t * t * P2.y,
      z: t1 * t1 * P0.z + 2 * t1 * t * p1z + t * t * P2.z,
    });
  }

  // Final pass: push any waypoints still inside planets to surface + margin
  const MARGIN = 0.5;
  for (let i = 0; i < result.length; i++) {
    const pt = result[i];
    for (let pi = 0; pi < planets.length; pi++) {
      if (pi === targetPlanetIndex || !planets[pi].alive) continue;
      const p = planets[pi];
      const dx = pt.x - p.position.x;
      const dy = pt.y - p.position.y;
      const dz = pt.z - p.position.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist > p.radius * 1.3) continue; // quick reject
      const surfR = getSurfaceRadius(p, { x: dx, y: dy, z: dz });
      const safeR = surfR + MARGIN;
      if (dist < safeR && dist > 0.001) {
        const scale = safeR / dist;
        result[i] = {
          x: p.position.x + dx * scale,
          y: p.position.y + dy * scale,
          z: p.position.z + dz * scale,
        };
      }
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Shared Geometry Cache (lazy-init, module-level)
// ---------------------------------------------------------------------------

let _geoCache: Record<string, THREE.BufferGeometry> | null = null;
function getGeoCache(): Record<string, THREE.BufferGeometry> {
  if (!_geoCache) {
    _geoCache = {
      outerFlame: new THREE.ConeGeometry(0.1, 0.6, 8),
      innerFlame: new THREE.ConeGeometry(0.04, 0.4, 6),
    };
  }
  return _geoCache;
}

// ---------------------------------------------------------------------------
// Build Drop Can Models
// ---------------------------------------------------------------------------


/** Stable per-team seed for the crashed can's debris so every peer lays the same scenery. */
function crashSeed(team: TeamPalette): number { let h = 0; for (const ch of team.name) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return h % 97; }
function buildDescentModel(team: TeamPalette): THREE.Group { return buildDropCanDescentBody(team); }
function buildCrashedModel(team: TeamPalette): THREE.Group { return buildDropCanCrashedBody(team, crashSeed(team)); }

function buildFlameGroup(): { flame: THREE.Group } {
  const geo = getGeoCache();
  const flame = new THREE.Group();

  const outerFlame = new THREE.Mesh(
    geo.outerFlame,
    new THREE.MeshBasicMaterial({ color: '#e88030', transparent: true, opacity: 0.7 }),
  );
  outerFlame.position.y = 0.3;
  outerFlame.userData.sharedGeometry = true;
  flame.add(outerFlame);

  const innerFlame = new THREE.Mesh(
    geo.innerFlame,
    new THREE.MeshBasicMaterial({ color: '#f0e0c0', transparent: true, opacity: 0.9 }),
  );
  innerFlame.position.y = 0.2;
  innerFlame.userData.sharedGeometry = true;
  flame.add(innerFlame);

  // No PointLight — toggling light visibility causes Three.js to recompile all PBR shaders
  // in the scene (light count changes). The bright MeshBasicMaterial cones are sufficient.

  // Start with zero scale (invisible but shader-compiled); approach phase scales up
  flame.scale.setScalar(0.001);
  return { flame };
}

// ---------------------------------------------------------------------------
// Create Drop Can Effect
// ---------------------------------------------------------------------------

export function createDropCanEffect(
  scene: THREE.Scene,
  planetPos: Vec3,
  surfaceNormal: Vec3,
  surfaceRadius: number,
  teamColor: TeamPalette,
  planets: PlanetState[],
  startDelay: number = 0,
): DropCanEffect {
  const descentModel = buildDescentModel(teamColor);
  const { flame } = buildFlameGroup();

  // Scale model relative to planet size
  const modelScale = surfaceRadius * 0.30;

  const rootGroup = new THREE.Group();

  const modelContainer = new THREE.Group();
  modelContainer.scale.setScalar(modelScale);
  modelContainer.add(descentModel);
  modelContainer.add(flame);
  rootGroup.add(modelContainer);

  // Pre-build crashed model at zero scale so WebGL compiles its shaders on first frame
  // (avoids lag spike when swapping to crashed model at landing)
  const crashed = buildCrashedModel(teamColor);
  const crashedContainer = new THREE.Group();
  crashedContainer.scale.setScalar(0.001);
  crashedContainer.add(crashed);
  rootGroup.add(crashedContainer);

  // Landing orientation: align local Y-up to surfaceNormal
  const normal3 = new THREE.Vector3(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);
  const surfaceQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    normal3,
  );

  // Arrival point above the surface
  const arrivalPoint: Vec3 = {
    x: planetPos.x + surfaceNormal.x * (surfaceRadius + ARRIVAL_ALT),
    y: planetPos.y + surfaceNormal.y * (surfaceRadius + ARRIVAL_ALT),
    z: planetPos.z + surfaceNormal.z * (surfaceRadius + ARRIVAL_ALT),
  };

  // Find target planet index
  let targetPlanetIndex = 0;
  for (let i = 0; i < planets.length; i++) {
    const pp = planets[i].position;
    if (Math.abs(pp.x - planetPos.x) < 0.01 &&
        Math.abs(pp.y - planetPos.y) < 0.01 &&
        Math.abs(pp.z - planetPos.z) < 0.01) {
      targetPlanetIndex = i;
      break;
    }
  }

  // Compute path eagerly — Bezier arc is instant, no deferral needed
  const pathVec3 = computeDropApproach(arrivalPoint, targetPlanetIndex, planets);
  const orbitalPath = pathVec3.map(p => new THREE.Vector3(p.x, p.y, p.z));

  // Position at path start and make visible immediately so WebGL compiles
  // all shaders on the first frame (avoids per-can lag spikes from lazy compilation)
  const hasDelay = startDelay > 0;
  rootGroup.position.copy(orbitalPath[0]);
  rootGroup.visible = true;
  scene.add(rootGroup);

  return {
    rootGroup,
    descentModel: modelContainer,
    crashedModel: crashedContainer,
    flame,
    flameLight: null,
    state: 'approach',
    stateTime: hasDelay ? -startDelay : 0,
    startDelay,
    approachSoundPlayed: false,
    landedSoundPlayed: false,
    orbitalPath,
    approachDuration: DROP_CAN_APPROACH_DURATION,
    planetCenter: new THREE.Vector3(planetPos.x, planetPos.y, planetPos.z),
    surfaceNormal: normal3.clone(),
    surfaceRadius,
    surfaceQuat,
    teamColor,
    _deferredArrival: null,
    _deferredTargetIdx: targetPlanetIndex,
    _deferredPlanets: null,
    _tmpPos: new THREE.Vector3(),
    _tmpVec: new THREE.Vector3(),
    _tmpVec2: new THREE.Vector3(),
    _tmpQuat: new THREE.Quaternion(),
    _tmpEuler: new THREE.Euler(),
  };
}

// ---------------------------------------------------------------------------
// Update Drop Can Effect (returns false when complete)
// ---------------------------------------------------------------------------

/** @deprecated Paths are now computed eagerly in createDropCanEffect. Kept for API compat. */
export function precomputeEffectPath(_effect: DropCanEffect): void {
  // no-op — paths computed eagerly at creation time
}

// ---------------------------------------------------------------------------

export function updateDropCanEffect(effect: DropCanEffect, dt: number, time: number): boolean {
  effect.stateTime += dt;

  // Waiting for stagger delay — hold position at path start
  if (effect.stateTime < 0) {
    return true;
  }

  const st = effect.stateTime;

  switch (effect.state) {
    case 'approach': {
      const rawT = Math.min(st / effect.approachDuration, 1);
      const t = smoothstep(rawT);

      const pathLen = effect.orbitalPath.length;
      const pathT = t * (pathLen - 1);
      const idx = Math.min(Math.floor(pathT), pathLen - 2);
      const frac = pathT - idx;

      const a = effect.orbitalPath[idx];
      const b = effect.orbitalPath[idx + 1];
      const pos = effect._tmpPos.set(
        a.x + (b.x - a.x) * frac,
        a.y + (b.y - a.y) * frac,
        a.z + (b.z - a.z) * frac,
      );
      effect.rootGroup.position.copy(pos);

      // Orient along path tangent
      const nextIdx = Math.min(idx + 1, pathLen - 1);
      const tang = effect._tmpVec.subVectors(effect.orbitalPath[nextIdx], a);
      if (tang.lengthSq() > 0.0001) {
        tang.normalize();
        effect._tmpVec2.copy(pos).add(tang);
        effect.descentModel.lookAt(effect._tmpVec2);
      }

      // Last 20%: slerp toward landing orientation
      if (rawT > 0.8) {
        const blend = (rawT - 0.8) / 0.2;
        effect.descentModel.quaternion.slerp(effect.surfaceQuat, blend);
      }

      effect.flame.scale.setScalar(1);

      if (rawT >= 1) {
        effect.state = 'descend';
        effect.stateTime = 0;
        effect.descentModel.quaternion.copy(effect.surfaceQuat);
      }
      break;
    }

    case 'descend': {
      const rawT = Math.min(st / DROP_CAN_DESCEND_DURATION, 1);
      const t = 1 - (1 - rawT) * (1 - rawT); // ease-out

      const startHeight = ARRIVAL_ALT;
      const modelScale = effect.surfaceRadius * 0.15;
      const endHeight = 0.22 * modelScale;
      const currentHeight = startHeight + (endHeight - startHeight) * t;

      const pos = effect._tmpPos
        .copy(effect.planetCenter)
        .addScaledVector(effect.surfaceNormal, effect.surfaceRadius + currentHeight);

      // Subtle sway
      const swayX = Math.sin(time * 2.3) * 0.015;
      const swayZ = Math.cos(time * 2.8) * 0.01;
      effect._tmpVec2.set(0, 1, 0);
      const sideDir = effect._tmpVec.crossVectors(effect.surfaceNormal, effect._tmpVec2);
      if (sideDir.lengthSq() < 0.001) {
        effect._tmpVec2.set(1, 0, 0);
        sideDir.crossVectors(effect.surfaceNormal, effect._tmpVec2);
      }
      sideDir.normalize();
      const fwdDir = effect._tmpVec2.crossVectors(sideDir, effect.surfaceNormal).normalize();
      pos.addScaledVector(sideDir, swayX);
      pos.addScaledVector(fwdDir, swayZ);

      effect.rootGroup.position.copy(pos);
      effect.descentModel.quaternion.copy(effect.surfaceQuat);

      // Tilt from sway
      effect._tmpEuler.set(Math.sin(time * 1.8) * 0.02, 0, Math.cos(time * 2.1) * 0.02);
      effect._tmpQuat.setFromEuler(effect._tmpEuler);
      effect.descentModel.quaternion.multiply(effect._tmpQuat);

      // Flame shrinking during descent
      const dist = (currentHeight - endHeight) / startHeight;
      const flameScale = 0.3 + dist * 0.7;
      effect.flame.scale.setScalar(flameScale);

      if (rawT >= 1) {
        effect.state = 'landed';
        effect.stateTime = 0;
        effect.flame.scale.setScalar(0.001);

        // Swap to crashed model (restore scale from warmup 0.001 → full size)
        if (effect.crashedModel) {
          const modelScale = effect.surfaceRadius * 0.30;
          effect.crashedModel.scale.setScalar(modelScale);
          effect.crashedModel.quaternion.copy(effect.surfaceQuat);
        }
        effect.descentModel.visible = false;

        // Position on surface
        effect._tmpPos
          .copy(effect.planetCenter)
          .addScaledVector(effect.surfaceNormal, effect.surfaceRadius);
        effect.rootGroup.position.copy(effect._tmpPos);
      }
      break;
    }

    case 'landed': {
      // Brief pause then done
      if (st > 0.5) {
        effect.state = 'done';
      }
      return true;
    }

    case 'done':
      return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Dispose
// ---------------------------------------------------------------------------

function disposeModel(group: THREE.Object3D): void {
  group.traverse(obj => {
    if (obj instanceof THREE.Mesh) {
      if (!obj.userData.sharedGeometry) obj.geometry.dispose();   // flame cones share the cache
      if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
      else obj.material.dispose();
    }
  });
}

export function disposeDropCanEffect(effect: DropCanEffect, keepCrashed: boolean = true): void {
  if (!keepCrashed) {
    if (effect.crashedModel) {
      effect.rootGroup.remove(effect.crashedModel);
      disposeModel(effect.crashedModel);
    }
  }

  disposeModel(effect.descentModel);

  // Remove descent model from root (crashed model stays if keepCrashed)
  effect.rootGroup.remove(effect.descentModel);

  if (!keepCrashed) {
    effect.rootGroup.parent?.remove(effect.rootGroup);
  }
}
